/**
 * « Composez votre tisane » (section pop-composer), version libre du 15/10/2026.
 *
 * Davy : « les gens sont trop enfermés » avec une plante + un complément. Un seul écran :
 * toutes les plantes sont proposées (les moments ne sont qu'un filtre), on en ajoute autant
 * qu'on veut, et la recette se règle en parts entières (Davy : pas de décimale), chanvre compris. Le chanvre est la
 * base ; s'il sort de 25 à 50 % (consigne de Davy), un conseil s'affiche, sans rien bloquer.
 * Pas de pourcentage à l'écran (Davy : « ça n'apporte rien ») : seulement les parts.
 * Le chanvre se retire d'un clic, comme une plante (Davy, 15/10/2026), et se remet.
 * « Tout ajouter » envoie le chanvre et les plantes en une seule requête, puis ouvre le tiroir.
 * En tête, des suggestions de recettes (Davy : « le client est un peu livré à lui-même ») : un clic
 * charge la recette déjà dosée, qu'on peut ensuite ajuster.
 */
import { signalerAjoutPanier } from '@theme/pop-cart';

const COULEURS = ['#22B07D', '#FFC233', '#FF6B4A', '#2E3192', '#8E6CC9', '#E38727', '#3FA7D6'];
const CHANVRE_MIN = 25;
const CHANVRE_MAX = 50;
const PAS = 1; // parts entières

const racine = document.querySelector('[data-pop-composer]');
if (racine) demarrer(racine);

/** @param {HTMLElement} racine */
function demarrer(racine) {
  const D = JSON.parse(racine.querySelector('[data-pop-cz-data]').textContent);
  const zonePlantes = racine.querySelector('[data-pop-cz-plantes]');
  const zoneRecette = racine.querySelector('[data-pop-cz-recette]');
  const parId = Object.fromEntries(D.plantes.map((p) => [p.id, p]));
  const st = { suggestion: -1, choix: [], chanvre: 1, chanvreRegle: false, avecChanvre: true, format: 0 };

  /** « chanvre:4, camomille:2 » → { chanvre: 4, choix: [{ id, parts }] } (plantes inconnues ignorées) */
  function lireComposition(texte) {
    const r = { chanvre: 1, choix: [] };
    String(texte || '')
      .split(/[,;\n]/)
      .map((x) => x.split(':').map((y) => y.trim()))
      .forEach(([id, n]) => {
        const parts = Number(String(n || '').replace(',', '.'));
        if (!id || !(parts > 0)) return;
        if (id === 'chanvre') r.chanvre = parts;
        else if (parId[id]) r.choix.push({ id, parts });
      });
    return r;
  }
  const suggestions = (D.suggestions || []).map((s) => ({ ...s, ...lireComposition(s.composition) })).filter((s) => s.choix.length);

  const esc = (s) => {
    const d = document.createElement('div');
    d.textContent = s ?? '';
    return d.innerHTML;
  };
  /** « 30 g » ne se coupe pas entre le nombre et l'unité */
  const souder = (s) => String(s ?? '').replace(/(\d) (g|kg|cl|ml)\b/g, '$1 $2');
  const euros = (cents) => (cents / 100).toFixed(2).replace('.', ',') + ' €';
  const cuilleres = (n) => String(n).replace('.', ',');
  const entier = (n) => Math.max(1, Math.round(n));

  /** Tant qu'on ne l'a pas réglé à la main, le chanvre suit : environ 40 % du mélange. */
  function chanvreAuto() {
    if (st.chanvreRegle) return;
    const plantes = st.choix.reduce((s, c) => s + c.parts, 0);
    st.chanvre = plantes ? entier(plantes * 0.67) : 1;
  }

  /** Pourcentages entiers qui font bien 100 */
  function pourcents(parts) {
    const total = parts.reduce((s, x) => s + x, 0) || 1;
    const bruts = parts.map((x) => (x / total) * 100);
    const ronds = bruts.map(Math.floor);
    let reste = 100 - ronds.reduce((s, x) => s + x, 0);
    bruts
      .map((x, i) => [x - Math.floor(x), i])
      .sort((a, b) => b[0] - a[0])
      .forEach(([, i]) => {
        if (reste-- > 0) ronds[i] += 1;
      });
    return ronds;
  }

  function rendrePlantes() {
    const liste = D.plantes;
    zonePlantes.innerHTML = `${suggestions.length ? `<p class="pop-cz__sugg-titre">${esc(D.suggestions_titre)}</p>
      <div class="pop-cz__suggestions">${suggestions
        .map(
          (s, i) => `<button type="button" class="pop-cz__sugg${st.suggestion === i ? ' is-on' : ''}${s.fonce ? ' is-fonce' : ''}" data-sugg="${i}" aria-pressed="${st.suggestion === i}"${s.couleur ? ` style="--sugg:${esc(s.couleur)}"` : ''}>
          <span class="pop-cz__sugg-plantes" aria-hidden="true">${s.choix.map((c) => (parId[c.id].img ? `<img src="${parId[c.id].img}" alt="" loading="lazy" width="38" height="38">` : '')).join('')}</span>
          <b>${esc(s.nom)}</b><span>${esc(s.sous_titre)}</span><em>${st.suggestion === i ? 'Choisie ✓' : 'Choisir →'}</em></button>`
        )
        .join('')}</div>` : ''}
      <div class="pop-cz__grid">${liste
        .map((p) => {
          const pris = st.choix.some((c) => c.id === p.id);
          return `<button type="button" class="pop-cz__card${pris ? ' is-selected' : ''}" data-id="${esc(p.id)}" aria-pressed="${pris}">
            ${p.img ? `<img src="${p.img}" alt="" loading="lazy" width="300" height="300">` : ''}
            <b>${esc(p.nom)}</b><span>${esc(p.ligne)}</span><em>${pris ? 'Dans ma tisane' : '+ Ajouter'}</em></button>`;
        })
        .join('')}</div>`;
    zonePlantes.querySelectorAll('[data-sugg]').forEach((el) =>
      el.addEventListener('click', () => {
        const s = suggestions[Number(el.dataset.sugg)];
        st.suggestion = Number(el.dataset.sugg);
        st.choix = s.choix.map((c) => ({ ...c }));
        st.chanvre = s.chanvre;
        st.chanvreRegle = true; // la recette garde son dosage de chanvre
        st.avecChanvre = true;
        rendre();
        if (window.matchMedia('(max-width: 989px)').matches) zoneRecette.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
    );
    zonePlantes.querySelectorAll('[data-id]').forEach((el) =>
      el.addEventListener('click', () => {
        const i = st.choix.findIndex((c) => c.id === el.dataset.id);
        if (i >= 0) st.choix.splice(i, 1);
        else st.choix.push({ id: el.dataset.id, parts: 1 });
        st.suggestion = -1;
        chanvreAuto();
        rendre();
      })
    );
  }

  function ligne(couleur, img, nom, role, parts, pct, cle, lien) {
    return `<div class="pop-cz__ligne"><span class="pop-cz__dot" style="background:${couleur}"></span>
      ${img ? `<img src="${img}" alt="" loading="lazy" width="48" height="48">` : ''}
      <div><b>${lien ? `<a href="${lien}">${esc(nom)}</a>` : esc(nom)}</b>${role ? `<small>${role}</small>` : ''}</div>
      <div class="pop-cz__dose" role="group" aria-label="Cuillères de ${esc(nom)}">
        <button type="button" data-moins="${cle}" aria-label="Moins">−</button>
        <span>${cuilleres(parts)}</span>
        <button type="button" data-plus="${cle}" aria-label="Plus">+</button>
      </div>
      <button type="button" class="pop-cz__retirer" data-retirer="${cle}" aria-label="Retirer ${esc(nom)}">×</button>
    </div>`;
  }

  function rendreRecette() {
    if (!st.choix.length) {
      zoneRecette.innerHTML = `<div class="pop-cz__box"><p class="pop-cz__h">Votre recette</p>
        <p class="pop-cz__sub">Partez d'une suggestion, ou ajoutez les plantes de votre choix : le chanvre est déjà dans la tasse.</p></div>`;
      return;
    }
    const plantes = st.choix.map((c) => ({ ...parId[c.id], parts: c.parts }));
    const avec = st.avecChanvre && !!D.chanvre;
    const pcts = pourcents([avec ? st.chanvre : 0, ...plantes.map((p) => p.parts)]);
    const pctChanvre = pcts[0];
    const conseil =
      avec && (pctChanvre < CHANVRE_MIN || pctChanvre > CHANVRE_MAX)
        ? `<p class="pop-cz__conseil">${pctChanvre < CHANVRE_MIN ? 'Conseil : ajoutez un peu de chanvre' : 'Conseil : ajoutez une plante ou retirez un peu de chanvre'} — pour l'équilibre, il fait entre un quart et la moitié du mélange.</p>`
        : '';

    const articles = [];
    if (avec && D.chanvre?.dispo) articles.push({ id: D.chanvre.variante, nom: `${D.chanvre.nom} · ${D.chanvre.format}`, prix: D.chanvre.prix });
    const absents = [];
    plantes.forEach((p) => {
      const v = p.produit?.variantes?.[st.format] || p.produit?.variantes?.[0];
      if (v?.dispo) articles.push({ id: v.id, nom: `${p.nom} · ${v.nom}`, prix: v.prix });
      else absents.push(p.nom);
    });
    const total = articles.reduce((s, a) => s + a.prix, 0);
    const formats = plantes.find((p) => p.produit)?.produit.variantes.map((v) => v.nom) || [];

    zoneRecette.innerHTML = `<div class="pop-cz__box">
        <p class="pop-cz__h">Votre recette</p>
        <p class="pop-cz__sub">En parts (une part = une cuillère) pour votre mélange. Réglez chaque plante à votre goût.</p>
        <div class="pop-cz__barre">${pcts.map((x, i) => `<span style="width:${x}%;background:${COULEURS[i % COULEURS.length]}"></span>`).join('')}</div>
        ${avec ? ligne(COULEURS[0], D.chanvre?.img, D.chanvre?.nom || 'Chanvre', 'La base', st.chanvre, pctChanvre, 'chanvre') : D.chanvre ? '<button type="button" class="pop-cz__zero pop-cz__remettre" data-remettre>+ Remettre le chanvre</button>' : ''}
        ${plantes.map((p, i) => ligne(COULEURS[(i + 1) % COULEURS.length], p.img, p.nom, '', p.parts, pcts[i + 1], p.id, p.page)).join('')}
        ${conseil}
        ${D.preparation ? `<p class="pop-cz__prep">${esc(D.preparation)}</p>` : ''}
        <button type="button" class="pop-cz__zero" data-zero>Repartir de zéro</button>
      </div>
      <div class="pop-cz__box pop-cz__panier">
        <p class="pop-cz__h">Dans votre panier</p>
        ${formats.length > 1 ? `<div class="pop-cz__formats" role="group" aria-label="Format des plantes">${formats
          .map((f, i) => `<button type="button" data-format="${i}" class="${i === st.format ? 'is-on' : ''}">${esc(f)}</button>`)
          .join('')}</div>` : ''}
        <ul>${articles.map((a) => `<li><span>${souder(esc(a.nom))}</span><span>${euros(a.prix)}</span></li>`).join('')}
          ${absents.map((n) => `<li class="pop-cz__absent"><span>${esc(n)}</span><span>bientôt disponible</span></li>`).join('')}</ul>
        <div class="pop-cz__total"><span>Total</span><span>${euros(total)}</span></div>
        <p class="pop-cz__livraison" data-livraison></p>
        <button type="button" class="button pop-cz__add" data-ajouter${articles.length ? '' : ' disabled'}>Tout ajouter au panier (${articles.length} produit${articles.length > 1 ? 's' : ''})</button>
      </div>`;

    zoneRecette.querySelector('[data-zero]')?.addEventListener('click', () => {
      Object.assign(st, { suggestion: -1, choix: [], chanvre: 1, chanvreRegle: false, avecChanvre: true });
      rendre();
    });
    const regler = (cle, delta) => {
      if (cle === 'chanvre') {
        st.chanvre = entier(st.chanvre + delta);
        st.chanvreRegle = true;
      } else {
        const c = st.choix.find((x) => x.id === cle);
        if (c) c.parts = entier(c.parts + delta);
        chanvreAuto();
      }
      st.suggestion = -1;
      rendre();
    };
    zoneRecette.querySelectorAll('[data-plus]').forEach((el) => el.addEventListener('click', () => regler(el.dataset.plus, PAS)));
    zoneRecette.querySelectorAll('[data-moins]').forEach((el) => el.addEventListener('click', () => regler(el.dataset.moins, -PAS)));
    zoneRecette.querySelector('[data-remettre]')?.addEventListener('click', () => {
      st.avecChanvre = true;
      rendreRecette();
    });
    zoneRecette.querySelectorAll('[data-retirer]').forEach((el) =>
      el.addEventListener('click', () => {
        if (el.dataset.retirer === 'chanvre') {
          st.avecChanvre = false;
          st.suggestion = -1;
          rendre();
          return;
        }
        st.choix = st.choix.filter((c) => c.id !== el.dataset.retirer);
        st.suggestion = -1;
        chanvreAuto();
        rendre();
      })
    );
    zoneRecette.querySelectorAll('[data-format]').forEach((el) =>
      el.addEventListener('click', () => {
        st.format = Number(el.dataset.format);
        rendreRecette();
      })
    );
    livraison(total);
    zoneRecette.querySelector('[data-ajouter]')?.addEventListener('click', (e) => ajouter(e.currentTarget, articles));
  }

  /** Ce qui manque pour la livraison offerte, panier actuel compris. */
  function livraison(total) {
    const el = zoneRecette.querySelector('[data-livraison]');
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        const reste = D.seuil - cart.total_price - total;
        if (el) el.textContent = reste > 0 ? `Plus que ${euros(reste)} pour la livraison offerte.` : 'Avec votre panier, la livraison est offerte.';
      })
      .catch(() => {});
  }

  /** @param {HTMLButtonElement} bouton */
  function ajouter(bouton, articles) {
    if (bouton.disabled) return; // pas de double clic
    bouton.disabled = true;
    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: articles.map((a) => ({ id: a.id, quantity: 1 })) }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return signalerAjoutPanier('pop-composer');
      })
      .catch(() => {
        bouton.textContent = 'Ajout impossible, réessayez';
      })
      .finally(() => {
        bouton.disabled = false;
      });
  }

  function rendre() {
    rendrePlantes();
    rendreRecette();
  }

  rendre();
}
